import React, { useState } from "react";
import Layout from "@/components/layout/Layout";
import { SEOHead } from "@/components/seo/SEOHead";
import { generateFAQSchema, generateBreadcrumbSchema, generateHowToSchema } from "@/lib/schema";
import { useGuideInsights } from "@/hooks/use-guide-insights";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Link } from "react-router-dom";
import {
  CheckCircle2,
  MapPin,
  Euro,
  Search,
  Luggage,
  FileText,
  Brain,
  ArrowRight,
  Sparkles,
  TrendingUp,
  Building2,
  ClipboardList,
  Users,
  ShieldCheck,
  Phone,
  ExternalLink,
  AlertCircle,
  Heart,
  Star,
} from "lucide-react";

// ── Chapter content ────────────────────────────────────────────────────────────
const CHAPTERS = [
  {
    id: "understand-needs",
    icon: Heart,
    number: "01",
    title: "Understanding Care Needs",
    summary:
      "Before you start visiting homes, get clarity on the level of care required — this shapes everything from cost to location.",
    points: [
      "Assess whether residential, dementia care, or palliative care is needed — your GP or HSE can help",
      "Consider mobility, medical needs, and social preferences (private vs shared room, activities)",
      "Involve the person in the decision as much as possible — dignity and preference matter",
      "Request a formal HSE needs assessment early — it's required for Fair Deal and can take weeks",
    ],
    cta: { label: "Browse nursing homes by care type", href: "/nursing-homes" },
  },
  {
    id: "types-of-homes",
    icon: Building2,
    number: "02",
    title: "Types of Nursing Homes in Ireland",
    summary:
      "Ireland has three main types of nursing homes — each with different ownership, pricing, and availability.",
    points: [
      "Public HSE homes: state-run, generally lower cost but limited availability and longer waitlists",
      "Private homes: most common, HIQA-regulated, participate in Fair Deal scheme with NTPF rates",
      "Voluntary / not-for-profit homes: often run by religious or charitable organisations, similar to private in most ways",
      "All participating homes must be HIQA-registered and have an NTPF-agreed price for Fair Deal residents",
    ],
    cta: { label: "View all HIQA-approved homes", href: "/nursing-homes" },
  },
  {
    id: "fair-deal",
    icon: Euro,
    number: "03",
    title: "The Fair Deal Scheme Explained",
    summary:
      "Fair Deal (NHSS) means the state pays the bulk of nursing home costs — you contribute based on income and assets.",
    points: [
      "You pay 80% of assessable income (e.g. pension) plus 7.5% of assets per year",
      "Your principal private residence is capped at 3 years (22.5% total) — the state covers the rest indefinitely",
      "The HSE financial assessment determines your exact contribution — apply as early as possible",
      "You can defer the property contribution until after death or sale of the home",
    ],
    cta: { label: "Calculate your Fair Deal contribution", href: "/fair-deal" },
  },
  {
    id: "finding-homes",
    icon: Search,
    number: "04",
    title: "Finding & Comparing Homes",
    summary:
      "Once you know the care level and county, compare homes on NTPF price, HIQA rating, amenities, and availability.",
    points: [
      "Search by county, care type, and Fair Deal eligibility on Grandpal — Ireland's most up-to-date directory",
      "Read HIQA inspection reports at hiqa.ie — look for 'Compliant' outcomes in key areas",
      "Compare NTPF weekly rates — these are the negotiated maximum a home can charge Fair Deal residents",
      "Consider location relative to family — proximity for visits dramatically impacts wellbeing",
    ],
    cta: { label: "Search nursing homes near you", href: "/nursing-homes" },
  },
  {
    id: "visiting",
    icon: ClipboardList,
    number: "05",
    title: "Visiting & What to Look For",
    summary:
      "Visit at least two or three homes before deciding. Arrive unannounced for your second visit to see the home as it really is.",
    points: [
      "Observe staff interactions — are they warm, unhurried, and respectful with residents?",
      "Check cleanliness, odour, noise levels, and whether residents appear engaged and content",
      "Ask about the staff-to-resident ratio and how agency staff use is managed",
      "Ask to see the activities schedule and sample menu — these reveal a lot about care philosophy",
    ],
    cta: { label: "Use our nursing home checklist", href: "/nursing-homes" },
  },
  {
    id: "moving-in",
    icon: Luggage,
    number: "06",
    title: "The Move & Transition",
    summary:
      "The first few weeks are the hardest — a little planning and the right support make an enormous difference.",
    points: [
      "Pack personal items, photos, and familiar objects to personalise the room from day one",
      "Plan frequent short visits in the first weeks — this is more reassuring than occasional long stays",
      "Keep communication with nursing home staff open — weekly check-ins with the key worker help",
      "Allow 4–6 weeks for adjustment — temporary distress is normal and usually resolves",
    ],
    cta: { label: "Book a consultation with a care advisor", href: "/consultations" },
  },
];

// ── FAQs ───────────────────────────────────────────────────────────────────────
const FAQS = [
  {
    question: "How do I know if a nursing home is safe?",
    answer:
      "All nursing homes in Ireland must be registered with HIQA (Health Information and Quality Authority). HIQA publishes inspection reports at hiqa.ie — look for homes with 'Compliant' outcomes across key areas like medication management, resident rights, and governance. Grandpal links to HIQA profiles for every home in our directory.",
  },
  {
    question: "How long does Fair Deal approval take?",
    answer:
      "The Fair Deal (NHSS) application typically takes 4–12 weeks from submission to approval. This includes the care needs assessment and financial assessment. Many families apply while still researching homes — approval is valid for 3 months from the date the HSE issues it.",
  },
  {
    question: "Can my parent choose their own nursing home?",
    answer:
      "Yes — under Fair Deal, you can choose any HIQA-registered nursing home that participates in the scheme. The key consideration is whether the home's NTPF rate fits within the HSE's contribution plus your personal contribution. If the home charges above the NTPF rate, you may be asked to pay a 'top-up'.",
  },
  {
    question: "Will we lose our family home?",
    answer:
      "Under Fair Deal, your principal private residence is treated as an asset — but only 7.5% per year is counted, and this is capped at 3 years (a maximum of 22.5% of its value). You can also opt to defer this contribution, meaning the HSE recovers it from the estate after death, rather than paying it immediately.",
  },
  {
    question: "What happens if the nursing home isn't a good fit?",
    answer:
      "Residents can move to a different nursing home at any time. Your Fair Deal approval follows you — contact the HSE's NHSS office to update your placement. It's worth visiting several homes before choosing to reduce the chance of needing to move.",
  },
  {
    question: "Are there grants for home modifications instead?",
    answer:
      "Yes — if residential care isn't yet necessary, the Housing Adaptation Grant and the Mobility Aid Grant (through your local authority) can fund home modifications. Home Care Packages from the HSE can also fund in-home care hours. These are worth exploring before committing to residential care.",
  },
];

// ── Live Stats Panel ───────────────────────────────────────────────────────────
function LiveStatsPanel() {
  const { insights, citations, isLoading, error } = useGuideInsights();

  return (
    <div className="rounded-2xl border border-border bg-card p-4 sm:p-6 space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-primary shrink-0" />
          <h3 className="text-base font-semibold text-foreground">
            Live 2026 Stats — Ireland Nursing Home Sector
          </h3>
        </div>
        <Badge variant="secondary" className="self-start sm:ml-auto text-xs">
          AI-powered
        </Badge>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-16 rounded-lg" />)}
        </div>
      ) : error || !insights ? (
        <div className="flex items-center gap-2 text-muted-foreground text-sm p-3 rounded-lg bg-muted">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>Live data temporarily unavailable.</span>
        </div>
      ) : (
        <>
          {insights.keyFact2026 && (
            <div className="flex items-start gap-2 p-3 rounded-xl bg-primary/5 border border-primary/15">
              <TrendingUp className="h-4 w-4 text-primary mt-0.5 shrink-0" />
              <p className="text-xs sm:text-sm text-foreground">{insights.keyFact2026}</p>
            </div>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
            {[
              { label: "Nursing Homes", value: insights.totalNursingHomes },
              { label: "Avg Weekly Cost", value: insights.averageWeeklyCost },
              { label: "Fair Deal Approved", value: insights.fairDealApprovalRate },
              { label: "Avg Wait Time", value: insights.averageWaitWeeks },
              { label: "HIQA Registered", value: insights.hiqa_registered },
            ].map((stat) => (
              <div key={stat.label} className="rounded-lg border border-border bg-background p-3">
                <p className="text-xs text-muted-foreground">{stat.label}</p>
                <p className="text-base sm:text-lg font-bold text-foreground mt-0.5">{stat.value}</p>
              </div>
            ))}
          </div>

          {citations.length > 0 && (
            <div className="flex flex-wrap gap-2 pt-1 border-t border-border">
              {citations.slice(0, 4).map((c, i) => {
                try {
                  const domain = new URL(c).hostname.replace("www.", "");
                  return (
                    <a key={i} href={c} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
                      <ExternalLink className="h-3 w-3" />{domain}
                    </a>
                  );
                } catch { return null; }
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────
export default function DigitalGuidePage() {
  const [activeChapter, setActiveChapter] = useState<string | null>(null);

  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: "Guides", url: "/guides" },
    { name: "Finding a Nursing Home in Ireland 2026", url: "/guides/finding-nursing-home-ireland-2026" },
  ];

  const schemas = [
    generateBreadcrumbSchema(breadcrumbs),
    generateFAQSchema(FAQS),
    generateHowToSchema({
      name: "How to Find a Nursing Home in Ireland 2026",
      description:
        "A complete, step-by-step guide to finding, evaluating, and moving into a nursing home in Ireland — including Fair Deal, HIQA, and practical family advice.",
      steps: CHAPTERS.map((c) => ({ name: c.title, text: c.summary })),
    }),
  ];

  return (
    <Layout>
      <SEOHead
        title="Finding a Nursing Home in Ireland 2026 | Complete Guide | Grandpal"
        description="Ireland's most comprehensive guide to finding a nursing home in 2026. Covers Fair Deal, HIQA, costs, what to look for, and how to make the move. Updated with live data."
        canonicalUrl="/guides/finding-nursing-home-ireland-2026"
        schema={schemas}
      />

      {/* ── Hero ── */}
      <section className="bg-gradient-to-b from-primary/5 to-background py-10 sm:py-16 md:py-20 border-b border-border">
        <div className="container max-w-4xl px-4">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <Badge variant="secondary" className="text-xs">Interactive Guide · 2026</Badge>
            <Badge variant="outline" className="text-xs text-muted-foreground">Updated with live data</Badge>
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-foreground leading-tight mb-4">
            Finding a Nursing Home<br className="hidden sm:block" /> in Ireland 2026
          </h1>
          <p className="text-base sm:text-xl text-muted-foreground max-w-2xl mb-6">
            Navigate the nursing home selection process with confidence — from understanding care needs
            to moving in. Written in plain English, updated for 2026.
          </p>

          {/* Quick nav */}
          <div className="flex flex-wrap gap-2 mb-8">
            {CHAPTERS.map((ch) => (
              <a
                key={ch.id}
                href={`#${ch.id}`}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-border bg-background text-xs font-medium hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors"
              >
                {ch.number} {ch.title}
              </a>
            ))}
          </div>

          {/* Hero CTAs */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button asChild size="lg" className="w-full sm:w-auto font-semibold">
              <Link to="/nursing-homes">
                <Search className="h-4 w-4 mr-2" />
                Search Nursing Homes
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="w-full sm:w-auto">
              <Link to="/fair-deal">
                <Euro className="h-4 w-4 mr-2" />
                Calculate Fair Deal Cost
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <div className="container max-w-4xl px-4 py-8 sm:py-12 md:py-16 space-y-10 sm:space-y-14">

        {/* Live stats */}
        <LiveStatsPanel />

        {/* ── What's inside strip ── */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { icon: FileText, label: "6 in-depth chapters", desc: "From care needs to moving in" },
            { icon: Star, label: "Live 2026 data", desc: "Sourced from HSE, HIQA & NTPF" },
            { icon: Users, label: "100% free", desc: "No signup required" },
          ].map((item) => (
            <div key={item.label} className="flex items-start gap-3 p-4 rounded-xl border border-border bg-card">
              <item.icon className="h-5 w-5 text-primary shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-foreground">{item.label}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* ── Chapters ── */}
        <div className="space-y-6 sm:space-y-8">
          {CHAPTERS.map((chapter) => {
            const Icon = chapter.icon;
            const isOpen = activeChapter === chapter.id;
            return (
              <section
                key={chapter.id}
                id={chapter.id}
                className="rounded-2xl border border-border bg-card overflow-hidden scroll-mt-20"
              >
                {/* Chapter header — always visible */}
                <button
                  onClick={() => setActiveChapter(isOpen ? null : chapter.id)}
                  className="w-full text-left p-4 sm:p-6 flex items-start gap-4 hover:bg-muted/30 transition-colors"
                >
                  <div className="shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <Icon className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-mono text-muted-foreground">{chapter.number}</span>
                      <h2 className="text-base sm:text-xl font-bold text-foreground">{chapter.title}</h2>
                    </div>
                    <p className="text-xs sm:text-sm text-muted-foreground mt-1 leading-relaxed">
                      {chapter.summary}
                    </p>
                  </div>
                  <ArrowRight
                    className={`h-4 w-4 text-muted-foreground shrink-0 mt-1 transition-transform duration-200 ${isOpen ? "rotate-90" : ""}`}
                  />
                </button>

                {/* Expandable content */}
                {isOpen && (
                  <div className="px-4 sm:px-6 pb-5 sm:pb-6 border-t border-border">
                    <ul className="space-y-3 mt-4 sm:mt-5">
                      {chapter.points.map((point, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                          <p className="text-sm text-foreground">{point}</p>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-5 pt-4 border-t border-border">
                      <Button asChild size="sm" className="w-full sm:w-auto">
                        <Link to={chapter.cta.href}>
                          {chapter.cta.label}
                          <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                        </Link>
                      </Button>
                    </div>
                  </div>
                )}
              </section>
            );
          })}
        </div>

        {/* ── Questions to ask checklist ── */}
        <QuestionsToAsk />

        {/* ── FAQs ── */}
        <section id="faqs">
          <div className="flex items-center gap-2 mb-5 sm:mb-6">
            <ShieldCheck className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
            <h2 className="text-xl sm:text-2xl font-bold text-foreground">Common Questions</h2>
          </div>
          <Accordion type="single" collapsible className="space-y-2 sm:space-y-3">
            {FAQS.map((faq, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                className="border border-border rounded-xl px-3 sm:px-5 bg-card"
              >
                <AccordionTrigger className="text-left text-sm sm:text-base font-medium text-foreground hover:no-underline py-4 sm:py-5">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-xs sm:text-sm pb-4 sm:pb-5">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>

        {/* ── Product CTAs ── */}
        <section className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="rounded-2xl bg-primary p-5 sm:p-7 text-primary-foreground space-y-3">
            <Building2 className="h-6 w-6 opacity-80" />
            <h3 className="text-lg font-bold">Find a nursing home</h3>
            <p className="text-sm text-primary-foreground/80">
              Browse 560+ HIQA-registered homes across every county in Ireland. Filter by Fair Deal,
              care type, and amenities.
            </p>
            <Button asChild className="bg-background text-foreground hover:bg-background/90 w-full font-semibold" size="sm">
              <Link to="/nursing-homes">Browse Homes <ArrowRight className="ml-1.5 h-3.5 w-3.5" /></Link>
            </Button>
          </div>
          <div className="rounded-2xl border border-border bg-card p-5 sm:p-7 space-y-3">
            <Euro className="h-6 w-6 text-primary" />
            <h3 className="text-lg font-bold text-foreground">Calculate Fair Deal cost</h3>
            <p className="text-sm text-muted-foreground">
              Get an instant estimate of your personal contribution under the Fair Deal scheme based
              on your income and assets.
            </p>
            <Button asChild className="w-full" size="sm">
              <Link to="/fair-deal#calculator">Use Calculator <ArrowRight className="ml-1.5 h-3.5 w-3.5" /></Link>
            </Button>
          </div>
          <div className="rounded-2xl border border-border bg-card p-5 sm:p-7 space-y-3">
            <Phone className="h-6 w-6 text-primary" />
            <h3 className="text-lg font-bold text-foreground">Talk to an expert</h3>
            <p className="text-sm text-muted-foreground">
              Get personalised advice from a care advisor — Fair Deal, choosing a home, or navigating
              the HSE application process.
            </p>
            <Button asChild variant="outline" className="w-full" size="sm">
              <Link to="/consultations">Book a Consultation <ArrowRight className="ml-1.5 h-3.5 w-3.5" /></Link>
            </Button>
          </div>
          <div className="rounded-2xl border border-border bg-card p-5 sm:p-7 space-y-3">
            <MapPin className="h-6 w-6 text-primary" />
            <h3 className="text-lg font-bold text-foreground">Browse by county</h3>
            <p className="text-sm text-muted-foreground">
              See availability, average rates, and local insights for every county across Ireland —
              from Dublin to Donegal.
            </p>
            <Button asChild variant="outline" className="w-full" size="sm">
              <Link to="/nursing-homes">Explore Counties <ArrowRight className="ml-1.5 h-3.5 w-3.5" /></Link>
            </Button>
          </div>
        </section>

      </div>
    </Layout>
  );
}

// ── Questions to Ask Component ─────────────────────────────────────────────────
function QuestionsToAsk() {
  const { insights, isLoading } = useGuideInsights();

  const fallbackQuestions = [
    "What is the nurse-to-resident ratio on day and night shifts?",
    "Is the home HIQA-registered with recent compliant inspection outcomes?",
    "What activities, outings, and social programmes are available?",
    "What is the visiting policy — including evenings and weekends?",
    "How is the Fair Deal contribution and any top-up charges handled?",
    "What is the process if my loved one's care needs change significantly?",
    "How are medication reviews and GP visits managed?",
    "What happens if I need to move my loved one to a different home?",
  ];

  const questions =
    !isLoading && insights?.questionsToAsk?.length
      ? [
          ...insights.questionsToAsk,
          ...fallbackQuestions.slice(insights.questionsToAsk.length),
        ]
      : fallbackQuestions;

  return (
    <section className="rounded-2xl border border-border bg-muted/30 p-4 sm:p-6 space-y-4">
      <div className="flex items-center gap-2">
        <ClipboardList className="h-5 w-5 text-primary" />
        <h2 className="text-lg sm:text-xl font-bold text-foreground">
          Questions to Ask on Your Visit
        </h2>
        {!isLoading && insights && (
          <Badge variant="secondary" className="ml-auto text-xs">AI-enhanced</Badge>
        )}
      </div>
      <p className="text-xs sm:text-sm text-muted-foreground">
        Bring this checklist when you visit nursing homes. A good home will answer every question openly.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
        {isLoading
          ? [...Array(8)].map((_, i) => <Skeleton key={i} className="h-10 rounded-lg" />)
          : questions.map((q, i) => (
              <div
                key={i}
                className="flex items-start gap-2.5 p-3 rounded-lg bg-background border border-border"
              >
                <CheckCircle2 className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" />
                <p className="text-xs sm:text-sm text-foreground">{q}</p>
              </div>
            ))}
      </div>
      <Button asChild className="w-full sm:w-auto mt-2" size="sm">
        <Link to="/nursing-homes">
          <Search className="mr-1.5 h-3.5 w-3.5" />
          Start searching nursing homes
        </Link>
      </Button>
    </section>
  );
}
